import { useState } from 'react';
import { Button, Dialog, DialogContent, Stack, Typography, TextField, MenuItem, Alert } from '@mui/material';
import api from 'api/api';
import serverCall from 'api/serverCall';
import Avatar from 'components/@extended/Avatar';
import { PopupTransition } from 'components/@extended/Transitions';
import { Edit } from 'iconsax-react';
import { useDispatch } from 'react-redux';
import { openSnackbar } from 'store/reducers/snackbar';
import { OrderTypeCode } from 'types/custom/order';

export interface EditOrderState {
  open: boolean;
  requestId: string;
  userId: string;
  currentQty: number;
  currentOrderType: OrderTypeCode | null;
  currentPrice: number;
  currentTriggerPrice: number;
  isBasket: boolean;
}

export const emptyEditOrderState: EditOrderState = {
  open: false,
  requestId: '',
  userId: '',
  currentQty: 0,
  currentOrderType: null,
  currentPrice: 0,
  currentTriggerPrice: 0,
  isBasket: false
};

const ORDER_TYPE_OPTIONS: { value: OrderTypeCode; label: string }[] = [
  { value: 'MARKET', label: 'Market' },
  { value: 'LIMIT', label: 'Limit' },
  { value: 'SL', label: 'SL Limit' },
  { value: 'SLM', label: 'SL Market' }
];

// Which fields each order type actually needs — mirrors the server-side validation.
const needsPrice = (type: OrderTypeCode) => type === 'LIMIT' || type === 'SL';
const needsTrigger = (type: OrderTypeCode) => type === 'SL' || type === 'SLM';

interface Props {
  state: EditOrderState;
  setState: (val: EditOrderState) => void;
  refetchSimulation: () => void;
}

export default function ChangeOrderQuantityModal({ state, setState, refetchSimulation }: Props) {
  const dispatch = useDispatch();
  const [quantity, setQuantity] = useState(state.currentQty?.toString() ?? '');
  const [orderType, setOrderType] = useState<OrderTypeCode>(state.currentOrderType ?? 'MARKET');
  const [price, setPrice] = useState(state.currentPrice ? state.currentPrice.toString() : '');
  const [triggerPrice, setTriggerPrice] = useState(state.currentTriggerPrice ? state.currentTriggerPrice.toString() : '');
  const [loading, setLoading] = useState(false);

  const close = () => setState(emptyEditOrderState);

  const warn = (message: string) =>
    dispatch(
      openSnackbar({
        open: true,
        close: true,
        message,
        variant: 'alert',
        alert: { color: 'warning' },
        anchorOrigin: { vertical: 'top', horizontal: 'center' }
      })
    );

  const handleSave = () => {
    const qtyNumber = Number(quantity);
    const priceNumber = Number(price);
    const triggerNumber = Number(triggerPrice);

    if (!state.requestId || !qtyNumber || qtyNumber <= 0) {
      warn('Please enter a valid quantity.');
      return;
    }

    // Validated here as well as on the server so the admin gets the message without
    // a round trip — the broker would reject these combinations outright.
    if (!state.isBasket) {
      if (needsPrice(orderType) && (!priceNumber || priceNumber <= 0)) {
        warn(`A ${orderType === 'SL' ? 'stop-loss limit' : 'limit'} order needs a price greater than zero.`);
        return;
      }
      if (needsTrigger(orderType) && (!triggerNumber || triggerNumber <= 0)) {
        warn('This order type needs a trigger price greater than zero.');
        return;
      }
    }

    setLoading(true);

    // A basket's legs each need their own type, so only quantity is editable there
    // and the pricing fields are left off entirely.
    const payload = state.isBasket
      ? { requestId: Number(state.requestId), quantity: qtyNumber, orderType: null, price: 0, triggerPrice: 0 }
      : {
          requestId: Number(state.requestId),
          quantity: qtyNumber,
          orderType,
          price: needsPrice(orderType) ? priceNumber : 0,
          triggerPrice: needsTrigger(orderType) ? triggerNumber : 0
        };

    serverCall.put(api.editPendingOrder(), payload, (response, error) => {
      setLoading(false);
      if (error) {
        dispatch(
          openSnackbar({
            open: true,
            close: true,
            message: error?.message ?? 'Failed to update the order request.',
            variant: 'alert',
            alert: { color: 'error' },
            anchorOrigin: { vertical: 'top', horizontal: 'center' }
          })
        );
      } else {
        refetchSimulation();
        dispatch(
          openSnackbar({
            open: true,
            close: true,
            message: `Order request ${state.requestId} updated.`,
            variant: 'alert',
            alert: { color: 'success' },
            anchorOrigin: { vertical: 'top', horizontal: 'center' }
          })
        );
        close();
      }
    });
  };

  return (
    <Dialog
      open={state.open}
      onClose={close}
      keepMounted
      fullWidth
      TransitionComponent={PopupTransition}
      maxWidth="xs"
      aria-labelledby="edit-order-request-title"
      aria-describedby="edit-order-request-description"
      sx={{ zIndex: 9998 }}
    >
      <DialogContent sx={{ mt: 2, my: 1 }}>
        <Stack alignItems="center" spacing={3.5}>
          <Avatar color="primary" sx={{ width: 72, height: 72, fontSize: '1.75rem' }}>
            <Edit variant="Bold" />
          </Avatar>

          <Stack spacing={2} sx={{ width: '100%' }}>
            <Typography variant="h4" align="center">
              Edit Order Request
            </Typography>

            <TextField
              fullWidth
              label="Quantity"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              inputProps={{ min: 1 }}
            />

            {state.isBasket ? (
              <Alert severity="info">Order type can&apos;t be changed on a basket order — each leg needs its own type.</Alert>
            ) : (
              <>
                <TextField
                  fullWidth
                  select
                  label="Order Type"
                  value={orderType}
                  onChange={(e) => setOrderType(e.target.value as OrderTypeCode)}
                  // The dialog sits at 9998 to clear the order drawer, so the menu's own
                  // portal has to be lifted above it or it renders behind the dialog.
                  SelectProps={{ MenuProps: { sx: { zIndex: 9999 } } }}
                >
                  {ORDER_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                {needsPrice(orderType) && (
                  <TextField
                    fullWidth
                    label="Price"
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    inputProps={{ min: 0, step: 0.05 }}
                  />
                )}

                {needsTrigger(orderType) && (
                  <TextField
                    fullWidth
                    label="Trigger Price"
                    type="number"
                    value={triggerPrice}
                    onChange={(e) => setTriggerPrice(e.target.value)}
                    inputProps={{ min: 0, step: 0.05 }}
                  />
                )}

                {orderType === 'MARKET' && (
                  <Alert severity="info">A market order executes at the prevailing price — no price or trigger is sent.</Alert>
                )}
              </>
            )}
          </Stack>

          <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
            <Button fullWidth onClick={close} color="secondary" variant="outlined" disabled={loading}>
              Cancel
            </Button>
            <Button fullWidth color="primary" variant="contained" onClick={handleSave} disabled={loading} autoFocus>
              {loading ? 'Saving...' : 'Update'}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
